import { type QueryClient, useQuery } from '@tanstack/react-query';
import { HeadContent, Link, Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { ChevronDown, Menu, Play, Settings as SettingsIcon, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { fetchVotableMatches } from '~/api/phases';
import { signOut, useSession } from '~/lib/auth';
import { ConsentBanner } from '~/ui/ConsentBanner';
import { HandleBadge } from '~/ui/HandleBadge';
import { LiveActivityPill } from '~/ui/LiveActivityPill';
import { WaveformDivider } from '~/ui/WaveformDivider';

type RouterContext = {
  queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const { data: session } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const isAuthed = Boolean(session);
  const isAdmin = session?.role === 'admin';
  const isAr = session?.role === 'ar' || session?.role === 'admin';
  const isSupporter = session?.plan === 'paid' || isAdmin;

  async function doSignOut() {
    await signOut();
    window.location.assign('/');
  }

  return (
    <div className="min-h-screen flex flex-col">
      <HeadContent />
      <header className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-40">
        <nav className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5">
          {/* Brand */}
          <Link
            to="/"
            aria-label="Prod Battle"
            className="group inline-flex items-center gap-2 font-black leading-none tracking-tight"
          >
            <span className="relative flex size-2.5 items-center justify-center">
              <span className="absolute inline-block size-2.5 animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-block size-2 rounded-full bg-primary" />
            </span>
            <span className="flex items-baseline gap-1 text-lg sm:text-xl">
              <span
                className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent transition-transform group-hover:-translate-y-0.5"
                style={{
                  backgroundSize: '200% 100%',
                  animation: 'brand-shimmer 4s linear infinite',
                }}
              >
                Prod
              </span>
              <span className="text-foreground transition-transform group-hover:translate-y-0.5">
                Battle
              </span>
            </span>
          </Link>

          {/* Live activity pill - hidden until first poll lands */}
          <LiveActivityPill />

          {/* Push the rest of the nav to the right */}
          <div className="flex-1" />

          {/* Desktop nav */}
          <div className="hidden items-center gap-3 md:flex">
            <PlayCta />
            <WaveformDivider />

            {/* Primary links: highest-traffic */}
            <div className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-wider text-muted-foreground">
              <NavLink to="/lobbies">lobbies</NavLink>
              <VoteNavLink />
              <NavLink to="/leaderboard">leaderboard</NavLink>
              {isAuthed && <NavLink to="/tournaments">tournaments</NavLink>}
            </div>

            <WaveformDivider />

            {/* "More" dropdown for secondary links */}
            <MoreDropdown isSupporter={isSupporter} isAuthed={isAuthed} />

            <WaveformDivider />

            <UserChip session={session} onSignOut={doSignOut} isAr={isAr} isAdmin={isAdmin} />
          </div>

          {/* Mobile: hamburger toggle */}
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center justify-center rounded-md border border-border p-2 text-muted-foreground md:hidden"
          >
            {menuOpen ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </nav>

        {/* Mobile full-screen drawer */}
        {menuOpen && (
          <div className="border-t border-border bg-background md:hidden">
            <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4">
              {/* Battle section */}
              <SectionLabel>Battle</SectionLabel>
              <div className="flex flex-col gap-2 font-mono text-sm uppercase tracking-wider text-muted-foreground">
                <Link
                  to="/play"
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex w-fit items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-primary-foreground"
                >
                  <Play className="size-3.5 fill-current" />
                  <span className="text-xs font-bold tracking-widest">PLAY</span>
                </Link>
                <NavLink to="/lobbies" onClick={() => setMenuOpen(false)}>
                  lobbies
                </NavLink>
                <VoteNavLink onClick={() => setMenuOpen(false)} mobile />
                <NavLink to="/feed" onClick={() => setMenuOpen(false)}>
                  feed
                </NavLink>
                <NavLink to="/leaderboard" onClick={() => setMenuOpen(false)}>
                  leaderboard
                </NavLink>
                {isAuthed && (
                  <NavLink to="/tournaments" onClick={() => setMenuOpen(false)}>
                    tournaments
                  </NavLink>
                )}
              </div>

              {/* Discover section */}
              <SectionLabel>Discover</SectionLabel>
              <div className="flex flex-col gap-2 font-mono text-sm uppercase tracking-wider text-muted-foreground">
                <NavLink to="/studio" onClick={() => setMenuOpen(false)}>
                  studio
                </NavLink>
                {isAuthed && (
                  <NavLink to="/briefs" onClick={() => setMenuOpen(false)}>
                    a&r
                  </NavLink>
                )}
                {isSupporter ? (
                  <NavLink to="/supporter/playlists" onClick={() => setMenuOpen(false)}>
                    playlists
                  </NavLink>
                ) : (
                  <NavLink to="/supporter/playlists" onClick={() => setMenuOpen(false)} muted>
                    playlists
                  </NavLink>
                )}
              </div>

              {/* Account section */}
              {isAuthed && (
                <>
                  <SectionLabel>Account</SectionLabel>
                  <div className="flex flex-col gap-2 font-mono text-sm uppercase tracking-wider text-muted-foreground">
                    <NavLink to="/inbox" onClick={() => setMenuOpen(false)}>
                      inbox
                    </NavLink>
                    <NavLink
                      to="/settings"
                      onClick={() => setMenuOpen(false)}
                      icon={<SettingsIcon className="size-3.5" />}
                    >
                      settings
                    </NavLink>
                    {isAr && (
                      <NavLink to="/ar/" onClick={() => setMenuOpen(false)} muted>
                        a&r dashboard
                      </NavLink>
                    )}
                    {isAdmin && (
                      <NavLink
                        to="/admin"
                        search={{ tab: 'overview' }}
                        onClick={() => setMenuOpen(false)}
                        muted
                      >
                        admin
                      </NavLink>
                    )}
                  </div>
                </>
              )}

              <div className="border-t border-border pt-3">
                <UserChip
                  session={session}
                  onSignOut={() => {
                    doSignOut();
                    setMenuOpen(false);
                  }}
                  isAr={isAr}
                  isAdmin={isAdmin}
                  compact
                />
              </div>
            </div>
          </div>
        )}
      </header>

      {!isAuthed && (
        <div className="border-b border-border bg-muted/30 font-mono text-[11px] tracking-wider text-muted-foreground">
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 py-1.5">
            <span className="hidden uppercase opacity-60 sm:inline">guest</span>
            <span>
              3 matches/day -{' '}
              <Link to="/auth/sign-up" className="underline hover:text-foreground">
                sign up free
              </Link>{' '}
              for unlimited matches, uploads, voting
            </span>
          </div>
        </div>
      )}

      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-border font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3">
          <span className="opacity-70">prod-battle - pre-alpha</span>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="hover:text-foreground">
              pricing
            </Link>
            <Link to="/blog" className="hover:text-foreground">
              blog
            </Link>
            <Link to="/rules" className="hover:text-foreground">
              rules &amp; faq
            </Link>
            {isAuthed && (
              <Link to="/support" className="hover:text-foreground">
                support
              </Link>
            )}
            <Link to="/legal" className="hover:text-foreground">
              legal
            </Link>
          </div>
        </div>
      </footer>
      <ConsentBanner />
    </div>
  );
}

// ─── Building blocks ─────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60">
      {children}
    </div>
  );
}

// Filled accent CTA for Play. Distinguishes the primary verb from the rest
// of the nav.
function PlayCta() {
  return (
    <Link
      to="/play"
      className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground transition-colors hover:bg-primary/90"
    >
      <Play className="size-3 fill-current" aria-hidden />
      <span>play</span>
    </Link>
  );
}

// Standard text nav link with a left accent bar that wipes in on hover.
type NavLinkProps = {
  to: string;
  search?: Record<string, unknown>;
  onClick?: () => void;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  muted?: boolean;
  srOnly?: string;
};
function NavLink({ to, search, onClick, children, icon, muted, srOnly }: NavLinkProps) {
  const linkProps = search ? { to, search: search as never } : { to };
  return (
    <Link
      {...(linkProps as { to: string })}
      onClick={onClick}
      className={`group relative inline-flex items-center gap-1.5 leading-none transition-colors ${muted ? 'text-muted-foreground/60 hover:text-foreground' : 'hover:text-foreground'}`}
    >
      <span
        aria-hidden
        className="absolute -left-2 inline-block h-3 w-[2px] origin-left scale-x-0 bg-primary transition-transform duration-150 group-hover:scale-x-100"
      />
      {icon}
      {srOnly && <span className="sr-only">{srOnly}</span>}
      {children}
    </Link>
  );
}

// VoteNavLink: only renders when there's at least one match currently in
// vote phase (audience drop-in target). Uses the same react-query key as
// /vote so the cache is shared. Polls every 10s. The render is null when
// the queue is empty so the nav stays slim during quiet periods.
function VoteNavLink({ onClick, mobile }: { onClick?: () => void; mobile?: boolean }) {
  const { data } = useQuery({
    queryKey: ['audience-matches'],
    queryFn: fetchVotableMatches,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
  const count = data?.length ?? 0;
  if (count === 0) return null;
  return (
    <Link
      to="/vote"
      onClick={onClick}
      className="group relative inline-flex items-center gap-1.5 leading-none text-foreground transition-colors"
    >
      <span
        aria-hidden
        className="absolute -left-2 inline-block h-3 w-[2px] origin-left scale-x-0 bg-primary transition-transform duration-150 group-hover:scale-x-100"
      />
      <span className="relative flex size-1.5 items-center justify-center" aria-hidden>
        <span className="absolute inline-block size-1.5 animate-ping rounded-full bg-emerald-400/60" />
        <span className="relative inline-block size-1 rounded-full bg-emerald-400" />
      </span>
      vote
      <span
        className={`rounded-full bg-primary/15 px-1.5 ${mobile ? 'text-xs' : 'text-[10px]'} font-bold leading-none text-primary`}
      >
        {count}
      </span>
    </Link>
  );
}

// "More" dropdown containing secondary nav links (feed, genres, playlists,
// upload). Sits between the primary cluster and the user chip on desktop.
function MoreDropdown({
  isSupporter,
  isAuthed,
}: {
  isSupporter: boolean;
  isAuthed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 font-mono text-[12px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
        aria-expanded={open}
        aria-label="More navigation links"
      >
        more
        <ChevronDown
          className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 min-w-[160px] overflow-hidden rounded-md border border-border bg-background shadow-lg">
          <DropdownLink to="/feed" onClick={() => setOpen(false)}>
            Feed
          </DropdownLink>
          <DropdownLink to="/studio" onClick={() => setOpen(false)}>
            Studio
          </DropdownLink>
          {isAuthed && (
            <DropdownLink to="/briefs" onClick={() => setOpen(false)}>
              A&R
            </DropdownLink>
          )}
          {isAuthed && (
            <DropdownLink to="/inbox" onClick={() => setOpen(false)}>
              Inbox
            </DropdownLink>
          )}
          <DropdownLink
            to="/supporter/playlists"
            onClick={() => setOpen(false)}
            muted={!isSupporter}
          >
            Playlists{!isSupporter && <span className="ml-1 text-[9px] opacity-50">supporter</span>}
          </DropdownLink>
        </div>
      )}
    </div>
  );
}

function DropdownLink({
  to,
  onClick,
  children,
  muted,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-3 py-2 font-mono text-[11px] uppercase tracking-wider hover:bg-muted hover:text-foreground ${muted ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}
    >
      {children}
    </Link>
  );
}

// Top-right user chip. Authed: shows handle + dropdown with settings, role
// links (A&R, admin), and sign-out. Unauthed: sign-in / sign-up buttons.
function UserChip({
  session,
  onSignOut,
  isAr,
  isAdmin,
  compact = false,
}: {
  session: ReturnType<typeof useSession>['data'];
  onSignOut: () => void;
  isAr: boolean;
  isAdmin: boolean;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!session) {
    return (
      <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : ''}`}>
        <HandleBadge />
        <Link
          to="/auth/sign-in"
          className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          sign in
        </Link>
        <Link
          to="/auth/sign-up"
          className="rounded-md bg-primary px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-primary-foreground"
        >
          sign up
        </Link>
      </div>
    );
  }

  const cleanHandle = session.handle?.replace(/^@+/, '') ?? null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/20 px-2.5 py-1 font-mono text-[11px] text-foreground hover:border-foreground/40"
      >
        <span className="size-1.5 rounded-full bg-emerald-400" aria-hidden />
        <span>@{cleanHandle ?? 'you'}</span>
        <span aria-hidden className="text-muted-foreground">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 min-w-[160px] overflow-hidden rounded-md border border-border bg-background shadow-lg">
          {cleanHandle && (
            <Link
              to="/$username"
              params={{ username: cleanHandle }}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              profile
            </Link>
          )}
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            settings
          </Link>
          {isAr && (
            <DropdownLink to="/ar" onClick={() => setOpen(false)}>
              a&r
            </DropdownLink>
          )}
          {isAdmin && (
            <DropdownLink to="/admin" onClick={() => setOpen(false)}>
              admin
            </DropdownLink>
          )}
          <div className="border-t border-border/50" />
          <button
            type="button"
            onClick={() => {
              onSignOut();
              setOpen(false);
            }}
            className="block w-full px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            sign out
          </button>
        </div>
      )}
    </div>
  );
}
